/**
 * fechas.js — leer los timestamps de divina como instantes reales.
 *
 * YA NO HAY COLUMNAS SIN ZONA (migración `pagos_fechas_con_zona`)
 * --------------------------------------------------------------
 * `pagos` era la última tabla con fechas `timestamp without time zone`
 * —`transaction_date`, `created_at`, `updated_at`, `next_payment_date`— y desde
 * esa migración son `timestamptz`. Hoy la base entrega el instante con su offset
 * y este módulo, para esas columnas, no tiene nada que arreglar: `instanteDeDb`
 * las deja pasar intactas.
 *
 * QUÉ ARREGLABA, Y POR QUÉ SIGUE ACÁ
 * ----------------------------------
 * Postgres DESCARTA el offset al escribir en una columna sin zona: guarda el
 * reloj de pared y pierde de dónde venía. Después `new Date(string)` sobre un
 * date-time sin offset lo interpreta como hora LOCAL del navegador, lo que en
 * Chile corría el instante 3 o 4 horas: el pago PY-2607-1259 se mostraba a las
 * 00:40 del 18 cuando ocurrió a las 20:40 del 17 — DESPUÉS de su propio
 * certificado.
 *
 * El módulo sobrevive a la migración porque la normalización es idempotente y
 * defensiva: un valor con zona pasa igual, y uno sin zona se lee como UTC. Sirve
 * para datos viejos exportados, para respuestas de terceros y para cualquier
 * columna que nazca sin zona en el futuro. Si un día se comprueba que ninguna
 * ruta puede entregar un valor naive, esto se puede borrar; hasta entonces es una
 * red que no cuesta nada.
 *
 * POR QUÉ EL INSTANTE Y NO LA HORA DE PARED
 * -----------------------------------------
 * Convertir un instante a fecha local es SIEMPRE unívoco. Lo ambiguo es el camino
 * inverso: la noche en que Chile atrasa el reloj, las 23:30 ocurren dos veces y
 * una hora de pared sin zona no distingue cuál de los dos instantes fue. Por eso
 * guardar el instante no es una preferencia de formato: es lo único que hace la
 * fecha recuperable.
 *
 * @see supabase migración `pagos_fechas_con_zona` — el arreglo de fondo
 *
 * CÓMO SE MUESTRA: HORA DE CHILE
 * ------------------------------
 * El formateo se delega en `src/styles/tokens/formatters.js`, que ya rinde en
 * `America/Santiago`. Este módulo no formatea nada: solo arregla el INSTANTE que
 * ese módulo recibe. Son dos problemas distintos —qué momento es, y cómo se
 * escribe— y mezclarlos es lo que los tenía rotos.
 *
 * @see src/styles/tokens/formatters.js — el render (zona de Chile), sin tocar
 * @see src/lib/yoolpay/formato.js — otro contrato: corta el DÍA en UTC a propósito
 */

// Con extensión explícita: webpack resuelve `src/lib` como ESM estricto y sin el
// `.js` falla el build (igual que en `src/lib/supabase.js`).
import { formatDateTime, formatDate } from '../styles/tokens/formatters.js';

/** Un `'YYYY-MM-DD'` suelto: día calendario, sin hora. */
const SOLO_FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

/** ¿El string ya declara su zona? Entonces el instante no es ambiguo. */
const TRAE_ZONA_RE = /([zZ]|[+-]\d{2}(:?\d{2})?)$/;

/**
 * Un offset de dos dígitos (`'…266+00'`) completado a `±HH:00`.
 *
 * Postgres emite esa forma abreviada y **JavaScript no la parsea**:
 * `Date.parse('2026-07-18T00:40:41.266+00')` da `NaN`. El valor trae su zona —o
 * sea el instante NO es ambiguo—, pero se perdía igual al leerlo. Completarlo es
 * lo único que falta para que sea legible; no cambia el instante.
 *
 * La API entrega `+00:00`, así que la app no ve esta forma; llega de datos
 * exportados a mano o de un cliente que hable SQL directo.
 */
const completarOffset = (iso) => iso.replace(/([+-]\d{2})$/, '$1:00');

/**
 * Un timestamp de la base → un string que `new Date()` lee como el instante
 * correcto.
 *
 * Lo que hace, en una línea: si al valor le falta la zona, se le pone `Z`,
 * porque lo guardado está en UTC (ver la cabecera del módulo).
 *
 * - `'2026-07-18T00:40:41.266'`       → `'2026-07-18T00:40:41.266Z'`
 * - `'2026-07-18 00:40:41.266'`       → `'2026-07-18T00:40:41.266Z'` (espacio de Postgres)
 * - `'2026-07-18T00:40:41.266+00:00'` → igual (ya trae zona, es `timestamptz`)
 * - `'2026-07-18'`                    → `'2026-07-18T00:00:00Z'`     (día calendario)
 *
 * @param {string|Date|null|undefined} valor
 * @returns {string|Date|null} el valor listo para `new Date()`, o `null`
 */
export const instanteDeDb = (valor) => {
  if (!valor) return null;
  if (typeof valor !== 'string') return valor;

  const limpio = valor.trim();
  if (!limpio) return null;

  if (SOLO_FECHA_RE.test(limpio)) return `${limpio}T00:00:00Z`;

  // Postgres separa fecha y hora con un espacio; ISO usa 'T'.
  const iso =
    limpio.includes(' ') && !limpio.includes('T')
      ? limpio.replace(' ', 'T')
      : limpio;

  // Con zona declarada el instante ya es unívoco: solo se completa el offset si
  // viene abreviado. Sin zona, se lee como UTC (ver la cabecera).
  return TRAE_ZONA_RE.test(iso) ? completarOffset(iso) : `${iso}Z`;
};

/**
 * El instante en milisegundos, para ordenar y comparar. `null` si no se puede
 * leer — así una fila sin fecha no se cuela como epoch 0 al principio del orden.
 *
 * @param {string|Date|null|undefined} valor
 * @returns {number|null}
 */
export const msDeDb = (valor) => {
  const normalizado = instanteDeDb(valor);
  if (!normalizado) return null;
  const t = new Date(normalizado).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * Fecha y hora de un timestamp de la base, en hora de Chile.
 *
 * @param {string|Date|null|undefined} valor
 * @param {Object} [options] opciones extra de `Intl.DateTimeFormat`
 */
export const fmtFechaHoraDb = (valor, options) =>
  formatDateTime(instanteDeDb(valor), options);

/**
 * Solo el día de un timestamp de la base, en hora de Chile.
 *
 * Cuidado: un instante cerca de medianoche UTC cae en el día ANTERIOR en Chile,
 * y eso es correcto — es el día que fue acá.
 *
 * @param {string|Date|null|undefined} valor
 * @param {Object} [options] opciones extra de `Intl.DateTimeFormat`
 */
export const fmtFechaDb = (valor, options) =>
  formatDate(instanteDeDb(valor), options);
